import type { SlotsToClasses, TypographySlots, TypographyVariantProps } from "@kirvanobr/theme";

import { useDOMRef } from "@kirvanobr/react-utils";
import { HTMLUIProps, PropGetter } from "@kirvanobr/system";
import { typography } from "@kirvanobr/theme";
import { Ref, useCallback, useMemo } from "react";
import { clsx } from "@kirvanobr/shared-utils";

type TypographyElement = HTMLParagraphElement | HTMLHeadingElement | HTMLSpanElement;

interface Props<T extends TypographyElement = HTMLParagraphElement>
  extends HTMLUIProps<"p" | "span" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6">,
    TypographyVariantProps {
  /**
   * Ref to the DOM node.
   */
  ref?: Ref<T>;

  /**
   * @example
   * ```tsx
   * <Typography classNames={{ base: "base-classes" }} />
   * ```
   */
  classNames?: SlotsToClasses<TypographySlots>;
}

export type UseTypographyProps<T extends TypographyElement = HTMLParagraphElement> = Props<T> &
  TypographyVariantProps;

export function useTypography<T extends TypographyElement = HTMLParagraphElement>(
  originalProps: UseTypographyProps<T>,
) {
  const { ref, as, className, classNames, variant, size, weight, color, ...otherProps } =
    originalProps;

  let Component;

  switch (variant) {
    case "title":
      switch (size) {
        case "t1":
          Component = as || "h1";
          break;
        case "t2":
          Component = as || "h2";
          break;
        case "t3":
          Component = as || "h2";
          break;
        case "t4":
          Component = as || "h4";
          break;
        case "t5":
          Component = as || "h5";
          break;
        case "t6":
          Component = as || "h6";
          break;
        case "t7":
          Component = as || "h6";
          break;
        default:
          Component = as || "h6";
          break;
      }
      break;
    case "body":
    case "subtitle":
      Component = as || "p";
      break;
    case "assistant":
      Component = as || "span";
      break;
    default:
      Component = as || "p";
      break;
  }

  const domRef = useDOMRef<T>(ref);

  const slots = useMemo(
    () =>
      typography({
        variant,
        size,
        weight,
        color,
        className,
      }),
    [variant, size, weight, color, className],
  );

  const baseStyles = clsx(classNames?.base, className);

  const getBaseProps: PropGetter = useCallback(
    (props = {}) => {
      return {
        ...props,
        ...otherProps,
        ref: domRef,
        className: slots.base({ class: baseStyles }),
      };
    },
    [otherProps, slots.base, classNames?.base],
  );

  return { Component, getBaseProps, variant };
}

export type UseTypographyReturn = ReturnType<typeof useTypography>;
